/**
 * Phase O.3 — LeaseBadge.
 *
 * Compact lease-state chip for the AutoPipelineBar / per-stage tab.
 * Shows: `attempt N/MAX · expires in M:SS` plus a pulsing dot when the
 * worker renewed within the last 5 seconds.
 *
 * Renders nothing if:
 *   - REACT_APP_LEASE_UI is not "true"
 *   - the stage has no active lease
 *   - the lease has already expired (clock skew / race) — the next poll
 *     will surface the new state
 *
 * Time math runs on a SINGLE 1s interval lifted at the parent's level
 * via the `nowMs` prop. This keeps per-stage re-renders cheap and avoids
 * a re-render storm on a busy board.
 */
import React from "react";

const LEASE_UI_ENABLED = process.env.REACT_APP_LEASE_UI === "true";

const _fmtDuration = (ms) => {
    const s = Math.max(0, Math.floor(ms / 1000));
    if (s >= 60) {
        const m = Math.floor(s / 60);
        const r = s % 60;
        return `${m}:${String(r).padStart(2, "0")}`;
    }
    return `${s}s`;
};

export const LeaseBadge = ({ lease, maxAttempts = 3, nowMs, onClick, className = "" }) => {
    if (!LEASE_UI_ENABLED) return null;
    if (!lease || !lease.lease_id) return null;
    const exp = Date.parse(lease.expires_at || "");
    if (!Number.isFinite(exp)) return null;
    const remaining = exp - (nowMs ?? Date.now());
    if (remaining <= 0) return null;

    const lastRenewed = Date.parse(lease.last_renewed_at || lease.acquired_at || "");
    const renewedRecently = Number.isFinite(lastRenewed)
        && (nowMs ?? Date.now()) - lastRenewed < 5000;

    const isCritical = remaining < 10_000;
    const isWarning = remaining < 60_000;
    const isFinalAttempt = lease.attempt >= maxAttempts;

    const tone = isCritical
        ? "bg-red-500/15 text-red-300 border-red-500/40 animate-pulse"
        : isWarning
            ? "bg-orange-500/15 text-orange-300 border-orange-500/40"
            : "bg-zinc-800/80 text-zinc-200 border-zinc-700";

    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={`lease-badge-${lease.stage_key}`}
            className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider border rounded-sm ${tone} ${className}`}
            title={onClick ? "Click for lease details" : ""}
        >
            <span
                aria-hidden="true"
                data-testid={`lease-badge-pulse-${lease.stage_key}`}
                className={`inline-block w-1.5 h-1.5 rounded-full ${
                    renewedRecently ? "bg-emerald-400 animate-pulse" : "bg-zinc-500"
                }`}
            />
            <span>LEASED</span>
            {lease.attempt > 1 && (
                <span
                    className={isFinalAttempt ? "text-amber-300" : ""}
                    data-testid={`lease-attempt-${lease.stage_key}`}
                >
                    {`· ${lease.attempt}/${maxAttempts}`}
                </span>
            )}
            <span data-testid={`lease-countdown-${lease.stage_key}`}>
                {`· ${_fmtDuration(remaining)}`}
            </span>
        </button>
    );
};

export const isLeaseUIEnabled = () => LEASE_UI_ENABLED;
